// Benchmark translations, scheduled scans, translation API
import { api } from './client';

// ============================================================================
// BENCHMARK TRANSLATIONS API
// ============================================================================

export interface BenchmarkTranslation {
  check_id: string;
  title: string;
  description: string;
  rationale: string;
  remediation: string;
  impact: string;
  references: string;
}

export interface CategoryTranslation {
  category_key: string;
  name: string;
  description: string;
  icon: string;
}

export const benchmarkTranslationsAPI = {
  getAll: (language: string = 'en') => {
    console.log('🌐 Fetching benchmark translations for language:', language);
    return api.get<{
      language: string;
      translations: Record<string, BenchmarkTranslation>;
      count: number;
    }>(`/benchmark-translations?lang=${language}`);
  },

  getByCheckId: (checkId: string, language: string = 'en') => {
    console.log('🌐 Fetching benchmark translation for check:', checkId, 'language:', language);
    return api.get<BenchmarkTranslation & { found: boolean }>(`/benchmark-translations/${checkId}?lang=${language}`);
  },

  getCategories: (language: string = 'en') => {
    console.log('🌐 Fetching category translations for language:', language);
    return api.get<{
      language: string;
      categories: Record<string, CategoryTranslation>;
      count: number;
    }>(`/benchmark-categories?lang=${language}`);
  },
};

// ============================================================================
// SCHEDULED SCANS API
// ============================================================================

export interface ScheduleListParams {
  project_id?: number;
  enabled?: boolean;
  page?: number;
  per_page?: number;
}

export interface ExecutionListParams {
  status?: string;
  limit?: number;
  offset?: number;
}

export const schedulesAPI = {
  getAll: (params?: ScheduleListParams) => {
    const queryParams = new URLSearchParams();
    if (params?.project_id) queryParams.append('project_id', String(params.project_id));
    if (params?.enabled !== undefined) queryParams.append('enabled', String(params.enabled));
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.per_page) queryParams.append('per_page', String(params.per_page));
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    console.log('📅 Fetching all schedules', queryString);
    return api.get(`/schedules${queryString}`);
  },

  getByProject: (projectId: number) => {
    console.log('📅 Fetching schedules for project:', projectId);
    return api.get(`/project/${projectId}/schedules`);
  },

  getById: (scheduleId: number) => {
    console.log('📅 Fetching schedule:', scheduleId);
    return api.get(`/schedules/${scheduleId}`);
  },

  create: (projectId: number, data: any) => {
    console.log('➕ Creating new schedule for project:', projectId, data.name);
    return api.post(`/project/${projectId}/schedules`, data);
  },

  update: (scheduleId: number, data: any) => {
    console.log('📝 Updating schedule:', scheduleId, data);
    return api.put(`/schedules/${scheduleId}`, data);
  },

  delete: (scheduleId: number) => {
    console.log('🗑️ Deleting schedule:', scheduleId);
    return api.delete(`/schedules/${scheduleId}`);
  },

  enable: (scheduleId: number) => {
    console.log('✅ Enabling schedule:', scheduleId);
    return api.post(`/schedules/${scheduleId}/enable`);
  },

  disable: (scheduleId: number) => {
    console.log('⏸️ Disabling schedule:', scheduleId);
    return api.post(`/schedules/${scheduleId}/disable`);
  },

  trigger: (scheduleId: number) => {
    console.log('🚀 Triggering schedule manually:', scheduleId);
    return api.post(`/schedules/${scheduleId}/trigger`);
  },

  duplicate: (scheduleId: number) => {
    console.log('📋 Duplicating schedule:', scheduleId);
    return api.post(`/schedules/${scheduleId}/duplicate`);
  },

  getExecutions: (scheduleId: number, params?: ExecutionListParams) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    console.log('📊 Fetching executions for schedule:', scheduleId);
    return api.get(`/schedules/${scheduleId}/executions${queryString}`);
  },

  getLatestExecution: (scheduleId: number) => {
    console.log('📊 Fetching latest execution for schedule:', scheduleId);
    return api.get(`/schedules/${scheduleId}/executions/latest`);
  },

  downloadExecutionReport: (scheduleId: number, executionNumber: number, format: string = 'pdf') => {
    console.log(`📥 Downloading ${format} report for schedule ${scheduleId}, execution #${executionNumber}`);
    return api.get(`/schedules/${scheduleId}/executions/${executionNumber}/report/${format}`, {
      responseType: 'blob'
    });
  },

  getLatestComparison: (scheduleId: number) => {
    console.log('🔄 Fetching latest comparison for schedule:', scheduleId);
    return api.get(`/schedules/${scheduleId}/comparison/latest`);
  },

  getTrend: (scheduleId: number, limit?: number) => {
    const queryString = limit ? `?limit=${limit}` : '';
    console.log('📈 Fetching trend for schedule:', scheduleId);
    return api.get(`/schedules/${scheduleId}/comparison/trend${queryString}`);
  },

  compareExecutions: (scheduleId: number, exec1Id: number, exec2Id: number) => {
    console.log('🔄 Comparing executions:', exec1Id, 'vs', exec2Id);
    return api.get(`/schedules/${scheduleId}/comparison/${exec1Id}/${exec2Id}`);
  },

  getCronPresets: () => {
    console.log('⏰ Fetching cron presets');
    return api.get('/schedules/cron/presets');
  },

  validateCron: (cronExpr: string) => {
    console.log('✔️ Validating cron expression:', cronExpr);
    return api.post('/schedules/cron/validate', { cron_expr: cronExpr });
  },

  getSchedulerStatus: () => {
    console.log('📊 Fetching scheduler status');
    return api.get('/scheduler/status');
  },

  getUpcoming: (limit?: number) => {
    const queryString = limit ? `?limit=${limit}` : '';
    console.log('📅 Fetching upcoming scheduled runs');
    return api.get(`/scheduler/upcoming${queryString}`);
  },
};

// ============================================================================
// TRANSLATION API
// ============================================================================

export interface TranslationEntry {
  entity_type: string;
  entity_id: number;
  field: string;
  language: string;
  value: string;
}

export interface SupportedLanguage {
  code: string;
  name: string;
  native: string;
}

export const translationsAPI = {
  getByType: (entityType: string, language: string) => {
    return api.get(`/translations?entity_type=${entityType}&language=${language}`);
  },

  getByEntity: (entityType: string, entityId: number, language?: string) => {
    const params = language ? `?language=${language}` : '';
    return api.get(`/translations/${entityType}/${entityId}${params}`);
  },

  getAllForLanguage: (language: string) => {
    return api.get(`/translations/all/${language}`);
  },

  getSupportedLanguages: () => {
    return api.get<SupportedLanguage[]>('/translations/languages');
  },

  set: (translation: TranslationEntry) => {
    return api.post('/translations', translation);
  },

  setBulk: (translations: TranslationEntry[]) => {
    return api.post('/translations/bulk', { translations });
  },

  deleteByEntity: (entityType: string, entityId: number) => {
    return api.delete(`/translations/${entityType}/${entityId}`);
  },

  delete: (entityType: string, entityId: number, field: string, language: string) => {
    return api.delete(`/translations/${entityType}/${entityId}/${field}/${language}`);
  },
};

// ============================================================================
// BASELINE SCHEDULING API
// ============================================================================

export interface BaselineScheduleConfig {
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  day_of_week?: number;
  day_of_month?: number;
  time: string;
  timezone: string;
  notify_on_change: boolean;
  auto_suppress_unchanged: boolean;
}

export const baselineSchedulingAPI = {
  getConfig: () => {
    console.log('⏰ Fetching baseline schedule configuration');
    return api.get<BaselineScheduleConfig>('/settings/baseline-schedule');
  },

  updateConfig: (config: Partial<BaselineScheduleConfig>) => {
    console.log('💾 Updating baseline schedule configuration');
    return api.put<BaselineScheduleConfig>('/settings/baseline-schedule', config);
  },

  triggerBaselines: () => {
    console.log('🚀 Triggering scheduled baselines');
    return api.post('/admin/baselines/trigger');
  },
};

export interface BaselineSnapshot {
  id: number;
  project_id: number;
  name: string;
  created_at: string;
  vulnerability_count: number;
  total_findings: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
}

export interface BaselineSchedule {
  id: number;
  project_id: number;
  name: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  day_of_week?: number;
  time: string;
  enabled: boolean;
  last_run?: string;
  next_run?: string;
  created_at: string;
}

export interface BaselineComparison {
  snapshot1: BaselineSnapshot;
  snapshot2: BaselineSnapshot;
  changes: BaselineChange[];
  new_vulnerabilities: BaselineChange[];
  resolved_vulnerabilities: BaselineChange[];
  changed_vulnerabilities: BaselineChange[];
  summary: {
    new_vulnerabilities: number;
    resolved_vulnerabilities: number;
    changed_vulnerabilities: number;
  };
}

export interface BaselineChange {
  id: number;
  type: 'new' | 'resolved' | 'changed';
  vulnerability_id: string;
  title: string;
  severity: string;
  details?: string;
}

export const baselineAPI = {
  getSnapshots: (projectId: number) => {
    console.log('📸 Fetching baseline snapshots for project:', projectId);
    return api.get<BaselineSnapshot[]>(`/projects/${projectId}/baselines/snapshots`);
  },

  getSchedules: (projectId: number) => {
    console.log('⏰ Fetching baseline schedules for project:', projectId);
    return api.get<BaselineSchedule[]>(`/projects/${projectId}/baselines/schedules`);
  },

  createSnapshot: (projectId: number) => {
    console.log('➕ Creating baseline snapshot for project:', projectId);
    return api.post<BaselineSnapshot>(`/projects/${projectId}/baselines/snapshots`);
  },

  compareSnapshots: (projectId: number, snapshot1Id: number, snapshot2Id: number) => {
    console.log('🔍 Comparing baseline snapshots:', snapshot1Id, 'vs', snapshot2Id);
    return api.get<BaselineComparison>(`/projects/${projectId}/baselines/compare?snapshot1=${snapshot1Id}&snapshot2=${snapshot2Id}`);
  },

  createSchedule: (projectId: number, schedule: Partial<BaselineSchedule>) => {
    console.log('➕ Creating baseline schedule for project:', projectId);
    return api.post<BaselineSchedule>(`/projects/${projectId}/baselines/schedules`, schedule);
  },

  deleteSchedule: (projectId: number, scheduleId: number) => {
    console.log('🗑️ Deleting baseline schedule:', scheduleId);
    return api.delete(`/projects/${projectId}/baselines/schedules/${scheduleId}`);
  },
};
